/**
 * NPPI / PII fields that must NEVER be sent to Owen (OpenAI) API calls.
 * Shown in the apply UI and enforced client + edge function.
 */

/** Sidebar heading for fields Owen does not receive. */
export const OWEN_PRIVATE_PANEL_TITLE = "What I don't see";

/** Sidebar heading for non-sensitive answers Owen uses. */
export const OWEN_SHARED_PANEL_TITLE = 'What I see';

/** Question / field ids blocked from AI payloads */
export const OWEN_BLOCKED_ANSWER_KEYS = new Set([
  'legalName',
  'firstName',
  'lastName',
  'name',
  'fullName',
  'ssn',
  'socialSecurityNumber',
  'taxpayerIdentifier',
  'email',
  'phone',
  'cellPhone',
  'homePhone',
  'workPhone',
  'employerPhone',
  'donorPhone',
  'dateOfBirth',
  'dob',
  'currentAddress',
  'previousAddress',
  'propertyAddress',
  'subjectPropertyAddress',
  'street',
  'streetAddress',
  'addressLine1',
  'addressLine2',
  'address',
]);

/** Borrower-facing label for each blocked field (Owen ignores list). */
const OWEN_IGNORE_LABEL_BY_KEY = {
  legalName: 'Your legal name',
  firstName: 'Your legal name',
  lastName: 'Your legal name',
  name: 'Your legal name',
  fullName: 'Your legal name',
  ssn: 'Your Social Security Number (SSN)',
  socialSecurityNumber: 'Your Social Security Number (SSN)',
  taxpayerIdentifier: 'Your Social Security Number (SSN)',
  email: 'Your email address',
  phone: 'Your phone number',
  cellPhone: 'Your phone number',
  homePhone: 'Your phone number',
  workPhone: 'Your phone number',
  employerPhone: 'Your phone number',
  donorPhone: 'Your phone number',
  dateOfBirth: 'Your date of birth',
  dob: 'Your date of birth',
  currentAddress: 'Your full property street address',
  previousAddress: 'Your full property street address',
  propertyAddress: 'Your full property street address',
  subjectPropertyAddress: 'Your full property street address',
  street: 'Your full property street address',
  streetAddress: 'Your full property street address',
  addressLine1: 'Your full property street address',
  addressLine2: 'Your full property street address',
  address: 'Your full property street address',
};

/** Label shown in the Owen ignores panel when the user answers a blocked question. */
export function getOwenIgnoreLabelForQuestionId(questionId) {
  if (!questionId || !isOwenBlockedQuestionId(questionId)) return null;
  if (OWEN_IGNORE_LABEL_BY_KEY[questionId]) return OWEN_IGNORE_LABEL_BY_KEY[questionId];

  const id = String(questionId).toLowerCase();
  if (id.includes('ssn')) return OWEN_IGNORE_LABEL_BY_KEY.ssn;
  if (id.includes('email')) return OWEN_IGNORE_LABEL_BY_KEY.email;
  if (id.includes('phone')) return OWEN_IGNORE_LABEL_BY_KEY.phone;
  if (id.includes('address')) return OWEN_IGNORE_LABEL_BY_KEY.currentAddress;
  if (id.includes('name') && !id.includes('program')) return OWEN_IGNORE_LABEL_BY_KEY.legalName;
  if (id.includes('birth') || id === 'dob') return OWEN_IGNORE_LABEL_BY_KEY.dateOfBirth;
  return 'This answer';
}

const ADDRESS_SUBKEYS_ALLOWED = new Set([
  'state',
  'county',
  'zip',
  'zipCode',
  'postalCode',
]);

export function isOwenBlockedQuestionId(questionId) {
  if (!questionId) return false;
  const id = String(questionId).toLowerCase();
  if (OWEN_BLOCKED_ANSWER_KEYS.has(questionId)) return true;
  if (id.includes('ssn') || id.includes('address') || id.includes('email') || id.includes('phone')) {
    return !id.includes('state') && !id.includes('county') && !id.includes('zip');
  }
  if (id.includes('name') && !id.includes('program')) return true;
  return false;
}

function sanitizeAddressValue(value) {
  if (!value || typeof value !== 'object') return undefined;
  const out = {};
  for (const [k, v] of Object.entries(value)) {
    if (ADDRESS_SUBKEYS_ALLOWED.has(k) && v != null && String(v).trim() !== '') {
      out[k] = v;
    }
  }
  return Object.keys(out).length ? out : undefined;
}

/**
 * Strip NPPI from answer maps before any Owen / OpenAI request.
 */
export function sanitizeAnswersForOwen(answers) {
  if (!answers || typeof answers !== 'object') return {};
  const out = {};
  for (const [key, value] of Object.entries(answers)) {
    if (OWEN_BLOCKED_ANSWER_KEYS.has(key) || isOwenBlockedQuestionId(key)) continue;
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      const addr = sanitizeAddressValue(value);
      if (addr) out[key] = addr;
      continue;
    }
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      out[key] = value;
    }
  }
  return out;
}

export function sanitizeAnswerLabelForOwen(questionId, label) {
  if (isOwenBlockedQuestionId(questionId)) {
    return '[Answer recorded — withheld from Owen]';
  }
  return label;
}

export function sanitizeTranscriptForOwen(transcript) {
  if (!Array.isArray(transcript)) return [];
  return transcript.map((msg) => {
    if (msg.role === 'user' && isOwenBlockedQuestionId(msg.questionId)) {
      return { role: 'user', text: '[Withheld for privacy]' };
    }
    return { role: msg.role, text: msg.text };
  });
}
